/*
 * Repeatable region can contain anything but other repateatable regions.
 */

import React from 'react';
import Base from './base.jsx';
 import {
  Mode,
  castDataType,
  DataType
} from '../common.jsx';
import Helpers from './helpers.jsx'
import Setting from "./setting.jsx";

import Resolver from '../component_resolver.jsx';
import { resolveComponentProps } from '../form_viewer.jsx';

export default class RepeatableRegion extends Base {
        static get CATEGORY() {
            return 'layout';
        }

        static get TYPE() {
            return 'repeatable_region';
        }

        static icon(){
            return <i className="fa fa-plus-square"></i>;
        }

        static get TRANSLATED_ATTRIBUTES() {
            return {
                label: 'field_default_label'
            };
        }

        static resolveRelatedAnswerNamesOrIds(content, repeatedFieldScope = '') {
          if (!content) { return [] }
          if (!this.ANSWER_DEPENDENT) { return [] }
          let names = [];
          // Add the current component
          if (content.name) { names.push(repeatedFieldScope + '[' + content.name + ']') }

          let childNames = [];
          if (content.component) {
            childNames = Resolver.componentClass(content.component.type).resolveRelatedAnswerNamesOrIds(content.component);
          }
          
          return [...names, ...childNames.flat()];
        }
        

        constructor(props, context) {
            super(props);

            let rowCount = 1;
            this.rowsWithReadOnlyFields = 0;

            let answer = ((this.props.answers || {})[(this.props.content || {}).name] || {});

            if (!this.inEditMode) {
                // Initial row count is the number of rows added to answers or min. required rows. Or at least 1.
                let requiredRowCount = ((this.props.content || {}).validations || {}).minRows || 0;
                let answerRowCount = (typeof answer.row_count != 'undefined') ? answer.row_count : Object.keys(answer).length;
                rowCount = Math.max(answerRowCount, requiredRowCount, 1);
                this.rowsWithReadOnlyFields = Object.keys(answer)
                  .filter((k) => k != "row_count")
                  .map((k) => Object.keys(answer[k]).filter((k) => k.match(/_readOnly/g)))
                  .filter((a) => a.length).length;
            }

            this.prefillAnswers = {};
            let hiddenPrefillRows = [];

            // Look for project-specific prefill answers if projectId is defined somehow, fallback to general prefillAnswers
            const prefillAnswers = (this.props.prefillAnswers || {})[this.getPrefillProjectId(context)] || this.props.prefillAnswers;

            if (!this.inEditMode && prefillAnswers) {
                // Look for report keys within nested components in this region
                // and build prefillAnswers for each row.
                // Number of rows is deduced from number of values per key so
                // this assumes a key doesn't exist multiple times per row as it shouldn't.
                // For fields inside optional_regions, prefill need to match with dependency values
                
                const componentsInside = this.constructor.nestedComponents(this.props.content);
                const componentsByReportKey = {};
                const keysInside = [];
                
                componentsInside.forEach((c) => {
                    if ((c.reportKey || '') != '') {
                        keysInside.push(c.reportKey);
                        componentsByReportKey[c.reportKey] = c;
                    }
                });
                
                // Find all optional_regions with dependencies and their reportKeys
                const optionalRegionInfo = {};
                const findOptionalRegions = (component) => {
                    if (!component) return;
                    
                    if (component.type === 'optional_region' && component.dependencies?.length > 0) {
                        const nestedComps = Resolver.componentClass(component.type).nestedComponents(component);
                        const reportKeys = nestedComps
                            .filter(nc => nc.reportKey)
                            .map(nc => nc.reportKey);
                        
                        if (reportKeys.length > 0) {
                            optionalRegionInfo[component.id] = {
                                dependencies: component.dependencies,
                                reportKeys: reportKeys
                            };
                        }
                    }
                    
                    const nested = Resolver.componentClass(component.type).nestedComponents(component);
                    nested.forEach(nc => findOptionalRegions(nc));
                };
                
                if (this.props.content.component) {
                    findOptionalRegions(this.props.content.component);
                }
                
                // Separate conditional (in optional_regions) vs unconditional reportKeys
                const conditionalReportKeys = new Set();
                Object.values(optionalRegionInfo).forEach(info => {
                    info.reportKeys.forEach(rk => conditionalReportKeys.add(rk));
                });
                
                // Distribute unconditional reportKeys normally by index
                keysInside.forEach((k) => {
                    if (!conditionalReportKeys.has(k) && prefillAnswers[k]) {
                        prefillAnswers[k].forEach((a, i) => {
                            const idx = this.rowsWithReadOnlyFields && i + 1 > this.rowsWithReadOnlyFields
                                    ? rowCount - 1 + i
                                    : i;
                            this.prefillAnswers['row-' + idx] = this.prefillAnswers['row-' + idx] || {};
                            this.prefillAnswers['row-' + idx][k] = [a];
                        });
                    }
                });
                
                // For conditional reportKeys, distribute based on dependency field values
                // Build mapping: { dependencyComponentId: { optionValue: [reportKeys...] } }
                const dependencyMap = {};
                Object.values(optionalRegionInfo).forEach(info => {
                    info.dependencies.forEach(dep => {
                        if (!dependencyMap[dep.componentId]) {
                            dependencyMap[dep.componentId] = {};
                        }
                        if (!dependencyMap[dep.componentId][dep.optionValue]) {
                            dependencyMap[dep.componentId][dep.optionValue] = [];
                        }
                        // Add all reportKeys from this optional_region
                        dependencyMap[dep.componentId][dep.optionValue].push(...info.reportKeys);
                    });
                });
                
                // Find dependency field's reportKey
                const dependencyComponentIds = Object.keys(dependencyMap);
                const dependencyReportKey = dependencyComponentIds
                    .map(id => componentsInside.find(c => c.id === id)?.reportKey)
                    .find(rk => rk && prefillAnswers[rk]);
                
                if (dependencyReportKey && prefillAnswers[dependencyReportKey]) {
                    // Track usage count per variant reportKey
                    const variantCounters = {};
                    conditionalReportKeys.forEach(rk => variantCounters[rk] = 0);
                    
                    // For each row, check dependency value and assign appropriate variant
                    prefillAnswers[dependencyReportKey].forEach((depValue, rowIndex) => {
                        const idx = this.rowsWithReadOnlyFields && rowIndex + 1 > this.rowsWithReadOnlyFields
                            ? rowCount - 1 + rowIndex
                            : rowIndex;
                        this.prefillAnswers['row-' + idx] = this.prefillAnswers['row-' + idx] || {};
                        
                        // Find which variant reportKeys match this dependency value.
                        // depValue may be an array (multi_dropdown_selection) or a scalar (dropdown_selection).
                        const selectedValues = Array.isArray(depValue) ? depValue : [depValue];

                        for (let depCompId of dependencyComponentIds) {
                            let foundAny = false;
                            selectedValues.forEach(val => {
                                const targetReportKeys = dependencyMap[depCompId][val];
                                if (targetReportKeys && targetReportKeys.length > 0) {
                                    foundAny = true;
                                    // Assign prefill values for all reportKeys in this optional_region
                                    targetReportKeys.forEach(targetReportKey => {
                                        if (prefillAnswers[targetReportKey]) {
                                            const valueIndex = variantCounters[targetReportKey];
                                            if (valueIndex < prefillAnswers[targetReportKey].length) {
                                                this.prefillAnswers['row-' + idx][targetReportKey] = [prefillAnswers[targetReportKey][valueIndex]];
                                                variantCounters[targetReportKey]++;
                                            }
                                        }
                                    });
                                }
                            });
                            if (foundAny) break;
                        }
                    });
                }

                rowCount =
                    Object.keys(answer).length || rowCount >= Object.keys(this.prefillAnswers).length
                        ? rowCount
                        : Object.keys(this.prefillAnswers).length;
            }

            let rows = [];

            for (let i = 0; i < rowCount; i++) {
                rows.push('row-' + i);
            }

            hiddenPrefillRows = Object.keys(this.prefillAnswers).filter(
                (rowId) => !rows.includes(rowId)
            );

            this.state = {
                rows: rows,
                hiddenPrefillRows,
            };

            this.deleteRow = this.deleteRow.bind(this);
            this.addRow = this.addRow.bind(this);
        }

        static defaultContent(language) {
            return Object.assign(super.defaultContent(language), {
                label: 'label',
                name: 'field_' + Helpers.randomString(),
                component: null,
                validations: {
                    minRows: null,
                    maxRows: null
                },
                reportKey: '',
                condensedMode: false,
                prettyView: {
                    hide: false,
                    show_label: false
                }
            });
        }

        static nestedComponents(parentComponentContent) {
            let c = (parentComponentContent || {}).component;
            return c ? [c].concat(Resolver.componentClass(c.type).nestedComponents(c)) : [];
        }

        static deleteNestedComponent(componentContent, componentId) {
            let found = false;
            let c = componentContent.component;

            if (c && c.id == componentId) {
                delete componentContent.component;
                found = true;
            } else if (c && Resolver.componentClass(c.type).deleteNestedComponent(c, componentId)) {
                found = true;
            }
            return found;
        }

        static isNested () {
            return true;
        }

        static disallowNesting() {
            return true;
        }

        static dropAllowed(dropArea, droppedContent, components, isNested) {
            if (isNested && this.disallowNesting()) {
                // Disallow dropping to anything nested
                alert(I18n.t('alert_nested_' + this.TYPE, {scope: 'fe'}));
                return false;
            }

            if (typeof dropArea.getAttribute == 'function' && dropArea.getAttribute('data-component-is-nested')) {
                return false;
            }

            if (components != undefined && this.disallowMultiple()) {
                for (var i = 0; i < components.length; i++) {
                    const comp = components[i];
                    if (
                        [
                            comp,
                            ...Resolver.componentClass(comp.type).nestedComponents(components[i]),
                        ].find((c) => c.type == this.TYPE && c.id != "component-drag-placeholder")
                    ) {
                        alert(I18n.t('alert_multiple_' + this.TYPE, {scope: 'fe'}));
                        return false;
                    }
                }
            }

            return true;
        }

        static dropComponent(dropArea, targetContent, droppedContent, components, isNested) {
            let dropped = false;

            // If condition is met, set the data attribute on dropArea to indicate nesting
            if (targetContent.component?.id == dropArea.getAttribute('data-component-id')) {
                dropArea.setAttribute('data-component-is-nested', true)
            }
            if (targetContent.id == dropArea.getAttribute('data-component-id') && Resolver.componentClass(droppedContent.type).dropAllowed(dropArea, droppedContent, components, this.isNested() || isNested)) {
                targetContent.component = droppedContent;
                dropped = true;
            } else {
                this.nestedComponents(targetContent).forEach((c) => {
                    if (Resolver.componentClass(c.type).dropComponent(dropArea, c, droppedContent, components, this.isNested() || isNested)) {
                        dropped = true;
                        return;
                    }
                });
            }
            return dropped;
        }

        renderView() {
            let rows = [];
            let mayDelete = (this.state.rows.length > ((this.props.content.validations || {}).minRows || 1));
            let mayAdd = (!(this.props.content.validations || {}).maxRows || this.state.rows.length < this.props.content.validations.maxRows);

            // User shouldn't be able to delete or add new rows if pre-filled value are used. (or any child component is pre-filled) or lockFormFill is enabled in monitoring report
            let isLocked = (this.props.content.lockAddDeleteWhenPrefilled || this.props.content.component?.lockEditWhenPrefilled || this.isReadOnlyAnswer());
            let disableDelete = this.props.mode != Mode.FILL || isLocked

            this.state.rows.forEach((rowId, i) => {
                let component = null;
                if (this.props.content.component) {
                    this.props.content.component.repeatedFieldScope = this.props.content.name + '[row-' + i + ']';
                    component = Resolver.createElement(this.props.content.component.type, resolveComponentProps(this.props.content.component, this.props,  
                      {
                        selectCallback: this.props.selectCallback,
                        selectedId: this.props.selectedId,
                        prefillAnswers: this.prefillAnswers[rowId],
                        repeatedFieldScope: this.props.content.component.repeatedFieldScope,
                        repeatedFieldRowIndex: i,
                      })
                    );
                } else if (this.inEditMode) {
                    component = <Helpers.DropArea componentId={this.props.content.id} />;
                }
                rows.push(
                    <div key={rowId} className="component-row">
                        <div className="component">
                            {component}
                        </div>
                        <div className="delete">
                            {mayDelete ?
                                <a href="#" className="btn btn-danger" disabled={this.props.mode != Mode.FILL || isLocked} onClick={disableDelete ?  (e) => { e.preventDefault(); } : this.deleteRow} data-row-id={rowId}><i className="icon-trash"></i></a>
                                : ''}
                        </div>
                    </div>
                );
            });

            return (
                <div className={this.props.content.condensedMode ? 'condensed_mode': ''}>
                    <label className={this.props.content.label_hidden ? 'hidden-label': ''}><span className="field-label">{this.getText('label')}</span></label>
                    {rows}
                    <button type="button" className="btn btn-primary" disabled={!mayAdd || this.props.mode != Mode.FILL || isLocked} onClick={mayAdd ? this.addRow : (e) => { e.preventDefault(); }}>
                        <i className="icon-add"></i> {I18n.t('add_row', {scope: 'fe', locale: this.props.language})}
                    </button>
                    {this.state.hiddenPrefillRows.length ? <button type="button" className="btn btn-info ml-1" disabled={!mayAdd || this.props.mode != Mode.FILL || isLocked} onClick={mayAdd ? this.addHiddenPrefillRows : (e) => { e.preventDefault(); }}>
                        <i className="fa fa-pencil-square-o"></i> {I18n.t('add_prefilled_rows', {scope: 'fe', locale: this.props.language})}
                    </button> : null}
                    <input type="hidden" name={this.fullFieldName() + '[row_count]'} defaultValue={rows.length} />
                </div>
            );
        }

        renderPrettyView() {
            let rows = [];

            this.state.rows.forEach((rowId, i) => {
                let component = null;
                if (this.props.content.component) {
                    component = Resolver.createElement(this.props.content.component.type, resolveComponentProps(this.props.content.component, this.props,  
                      {
                        selectCallback: this.props.selectCallback,
                        selectedId: this.props.selectedId,
                        prefillAnswers: this.prefillAnswers[rowId],
                        repeatedFieldScope: this.props.content.name + '[row-' + i + ']',
                        repeatedFieldRowIndex: i,
                      })
                    );
                } else if (this.inEditMode) {
                    component = <Helpers.DropArea componentId={this.props.content.id} />;
                }
                rows.push(
                    <div key={rowId} className="component-row">
                        <div className="component">
                            {component}
                        </div>
                    </div>
                );
            });

            const { hide = false, show_label = false } = this.props.content.prettyView || {};

            return (hide ? null :
                <div>
                    {show_label &&
                        <label>
                            <span className="field-label">{this.getText('label')}</span>
                        </label>
                    }
                    {rows}
                </div>
            );
        }

        renderSettings() {
            let validations = this.props.content.validations || {};

            return (
                <div>
                    <Setting.TextField name="label" label={I18n.t('field_label', {scope: 'fe'})} value={this.getText('label')} onChange={this.updateSettings} />
                    <Setting.Checkbox name="label_hidden" className="label-hidden-check" label={I18n.t('field_label_hide', {scope: 'fe'})} value={this.props.content.label_hidden} onChange={this.updateSettings} />
                    <Setting.Checkbox name='condensedMode' label={I18n.t('field_condensed_mode', {scope: 'fe'})}  description={I18n.t('field_condensed_mode_description', {scope: 'fe'})}  value={this.props.content.condensedMode} onChange={this.updateSettings} />
                    <div className="setting">
                        <label>
                            {I18n.t('repeatable_region_required_rows', {scope: 'fe'})}
                        </label>
                        <div className="validation-range">
                            <div className="value">
                                <input type="text" ref="minRows" name="minRows" className="form-control" defaultValue={validations.minRows} placeholder={I18n.t('validation_min', {scope: 'fe'})} onKeyUp={this.updateSettings} autoComplete={this.getAutoComplete()} />
                            </div>
                            <div className="dash">
                                &mdash;
                            </div>
                            <div className="value">
                                <input type="text" ref="maxRows" name="maxRows" className="form-control" defaultValue={validations.maxRows} placeholder={I18n.t('validation_max', {scope: 'fe'})} onKeyUp={this.updateSettings} autoComplete={this.getAutoComplete()} />
                            </div>
                        </div>
                        <p className="description">
                            {I18n.t('repeatable_region_required_rows_description', {scope: 'fe'})}
                        </p>
                    </div>
                    <Setting.TextField
                      name="reportKey"
                      label={I18n.t('field_report_key', {scope: 'fe'})}
                      value={this.props.content.reportKey}
                      description={I18n.t('field_report_key_description', {scope: 'fe'})}
                      {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
                    />
                    {AppConfig.features.form_prefill ? <Setting.Checkbox name="lockAddDeleteWhenPrefilled" label={I18n.t('field_lock_edit_when_prefilled', {scope: 'fe'})} description={I18n.t('field_lock_add_delete_when_prefilled_description', {scope: 'fe'})} value={this.props.content.lockAddDeleteWhenPrefilled} onChange={this.updateSettings} /> : null}
                </div>
            );
        }

        deleteRow(event) {
            if (this.props.mode == Mode.FILL && this.state.rows.length > 1 && confirm(I18n.t('confirm_delete_repeatable_region_row', {scope: 'fe', locale: this.props.language}))) {
                let rowId = event.currentTarget.getAttribute('data-row-id');
                let rows = this.state.rows;
                let rowIndex = this.state.rows.indexOf(rowId);
                rows.splice(rowIndex, 1);
                rows = rows.map((row, index) => 'row-' + index); // Remove gaps in row indexes after deletion
                (this.props.deleteRepeatableRegionAnswerCallback || (() => {}))(this.props.content.name, rowIndex);
                this.setState({
                    rows: rows
                });
            } else {
                jQuery(event.currentTarget).blur();
            }
            event.preventDefault();
        }

        addRow(event) {
            if (this.props.mode == Mode.FILL) {
                let lastRowId = this.state.rows[this.state.rows.length - 1];
                let newRowId = 'row-' + (parseInt(lastRowId.split('-')[1], 10) + 1);
                this.setState({
                    rows: this.state.rows.concat([newRowId])
                });
                this.props.updateAnswerCallback(this.props.content.name + '[' + newRowId + ']', {});
            }
            jQuery(event.currentTarget).blur();
            event.preventDefault();
        }

        addHiddenPrefillRows = (e) => {
            e.preventDefault();
            let hiddenPrefillRows = []
            let oldrowCount;
            const oldAnswers = { ...(this.props.answers[this.props.content.name] || {}) };
            let newAnswers = { ...oldAnswers };

            if (this.props.mode == Mode.FILL) {
                this.setState(prevState => {
                    hiddenPrefillRows = prevState.hiddenPrefillRows;
                    oldrowCount = prevState.rows.length;

                    return {
                        rows: prevState.rows.concat(hiddenPrefillRows),
                        hiddenPrefillRows: [],
                    };
                }, () => {
                    // If repeatable region has been filled and contains readOnly fields,
                    // insert hidden rows after current readOnly rows, so all of them will be consecutively.
                    if (this.rowsWithReadOnlyFields) {
                        const oldPrefillAnswers = { ...(this.prefillAnswers || {}) };

                        for (let i = this.rowsWithReadOnlyFields; i < oldrowCount; i++) {
                            const oldRowId = hiddenPrefillRows[i - this.rowsWithReadOnlyFields]; // oldRowId from 'hiddenPrefillRows'
                            const newRowId = `row-${i}`;
                            const replacedRowNewId = `row-${i + this.rowsWithReadOnlyFields}`;

                            // Move existing non-locked rows to new location
                            newAnswers[replacedRowNewId] = { ...(oldAnswers[newRowId] || {}) };
                            this.prefillAnswers[replacedRowNewId] = { ...(oldPrefillAnswers[newRowId] || {}) };

                            // Move hidden row to new location.
                            if (oldRowId !== void 0) {
                                this.prefillAnswers[newRowId] = { ...this.prefillAnswers[oldRowId] };
                                delete this.prefillAnswers[oldRowId];
                                delete newAnswers[newRowId];
                            }
                        }

                        newAnswers.row_count = this.state.rows.length.toString();
                        this.props.updateAnswerCallback(this.props.content.name, newAnswers);
                    }
                })
            }
        }

        beforeUpdateSettings(content) {
            content.condensedMode = castDataType(content.condensedMode, DataType.BOOLEAN);

            content.validations = {};

            if ((content.minRows || '').length > 0) {
                content.validations.minRows = castDataType(content.minRows, DataType.INTEGER);
            }
            if ((content.maxRows || '').length > 0) {
                content.validations.maxRows = castDataType(content.maxRows, DataType.INTEGER);

                // This shouldn't be done if maxRows is left empty.
                if ((content.validations.maxRows || 0) < (content.validations.minRows || 0)) {
                    content.validations.maxRows = content.validations.minRows;
                }
            }
            content.lockAddDeleteWhenPrefilled = castDataType(content.lockAddDeleteWhenPrefilled, DataType.BOOLEAN);
            content.label_hidden = castDataType(content.label_hidden, DataType.BOOLEAN);

            // These are only used for building validations object.
            // Deleting them here so they don't cause confusion in the form json.
            // This won't remove the attributes from existing forms.
            delete content.minRows;
            delete content.maxRows;
        }
}
